import React, { Component } from 'react';
import PropTypes from 'prop-types';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import appConfig from 'config/app.config';
import apiHelper from 'helpers/api-helper';
import {getText} from 'helpers/text-helper';
import {branchesData} from 'data/branches-data';
import {themesData} from 'data/themes-data';
import Loading from 'components/loading/loading';
import Settings from 'components/settings/settings';
import Stats from 'components/stats/stats';
import CreateGame from 'components/create-game/create-game';
import GameIntro from 'components/game-intro/game-intro';
import GameGroups from 'components/game-groups/game-groups';
import GameStatus from 'components/game-status/game-status';
import GameResultsIntro from 'components/game-results-intro/game-results-intro';
import GameResults from 'components/game-results/game-results';
import GameResultsOutro from 'components/game-results-outro/game-results-outro';
import GameExperiment from 'components/game-experiment/game-experiment';
import GameExperimentPresentation from 'components/game-experiment-presentation/game-experiment-presentation';
import GameEnd from 'components/game-end/game-end';
import Button from 'components/ui/button/button';


class FacilitatorController extends Component {
	constructor(props) {
		super(props);
		this.state = {
			isLoading: true,
			isAdmin: false,
			page: 'settings',
			game: null,
			groups: []
		};
		this.unsubscribeGame = null;
		this.unsubscribeGroups = null;
	}

	/**
	 * Component mounted
	 */
	componentDidMount = () => {
		/* Check if admin */
		this.checkIfAdmin();

		/* Subscribe to faclitator's game */
		this.subscribeToGame().then(() => {
			if (this.state.game) {
				this.setState({isLoading: false});
			} else {
				this.setState({isLoading: false, page: 'create-game'});
			}
		});
	};

	/**
	 * Component will unmount
	 */
	componentWillUnmount = () => {
		/* Unsubscribe from game */
		if (this.unsubscribeGame !== null) this.unsubscribeGame();

		/* Unsubscribe from groups */
		if (this.unsubscribeGroups !== null) this.unsubscribeGroups();
	};

	/**
	 * Create new game 
	 * Delete old game and its groups (if they exist)
	 */
	createNewGame = (branchId, themeId) => {
		return new Promise((resolve) => {
			apiHelper('facilitator/create-game', {
				facilitatorId: this.props.userId, 
				branchId, 
				themeId
			}).then((response) => {
				if (response.status === 'success') {
					/* New game created, subscribe to new game (and its groups) */
					this.subscribeToGame().then(() => {
						resolve({status: 'success'});
					}).catch((error) => {
						/* Error: Game subscription error */
						resolve({status: 'error', error});
					});
				} else {
					/* Error: New game not created */
					resolve({status: 'error', error: response.error.message});
				}
			}).catch((error) => {
				//* Error: New game not created */
				resolve({status: 'error', error: error.error.message});
			});
		});
	};

	/**
	 * Subscribe to game
	 */
	subscribeToGame = () => {
		/* Cancel previous subscription */
		if (this.unsubscribeGame !== null) this.unsubscribeGame();

		return new Promise((resolve) => {
			const db = firebase.firestore();
			const query = db.collection(appConfig.gamesDbName).where('facilitatorId', '==', this.props.userId);			
			this.unsubscribeGame = query.onSnapshot((querySnapshot) => {
				let game = null;
				querySnapshot.forEach((doc) => {
					if (game) return;
					if (
						doc.data() && 
						doc.data().branchId &&
						branchesData.some((branchData) => {return branchData.id === doc.data().branchId;}) &&
						doc.data().themeId &&
						themesData.some((themeData) => {return themeData.id === doc.data().themeId;})
					) {
						game = {id: doc.id, ...doc.data()};
					}
				});
				const startNewGroupSubscription = (game && (!this.state.game || this.state.game.id !== game.id) 
					? true : false);
				this.setState({game: game}, () => {
					if (startNewGroupSubscription) {
						this.subscribeToGroups(game.id).then(() => {
							resolve({status: 'success'});	
						});
					} else {
						resolve({status: 'success'});
					}
				});
			});
		});
	};

	/**
	 * Subscribe to groups of game
	 */
	subscribeToGroups = (gameId) => {
		/* Cancel previous subscription */
		if (this.unsubscribeGroups !== null) this.unsubscribeGroups();
		
		/* Subscribe  */
		const db = firebase.firestore();
		return new Promise((resolve) => {
			this.unsubscribeGroups = db.collection(appConfig.groupsDbName).where('gameId', '==', gameId)
				.onSnapshot((querySnapshot) => {
					let groups = [];
					querySnapshot.forEach((doc) => {groups.push({id: doc.id, ...doc.data()});});
					this.setState({groups: groups}, () => {resolve({status: 'success'});});
				});			
		});
	};

	/**
	 * Check if admin
	 */
	checkIfAdmin = () => {
		const db = firebase.firestore();
		const lowercaseEmail = this.props.email.toLowerCase();
		db.collection(appConfig.adminsDbName).where('email', '==', lowercaseEmail).get().then((docs) => {
			if (docs.size > 0) {
				this.setState({isAdmin: true});
			}
		});
	};


	/**
	 * Go to page
	 * @param {string} page 
	 */
	goToPage = (page) => {
		this.setState({page: page});
	};

	/**
	 * Render component
	 */
	render = () => {
		/* Loading */
		if (this.state.isLoading) {
			return (
				<Loading 
					role="facilitator"
					languageId={this.props.languageId}
					handleLogout={this.props.handleLogout} 
				/>
			);
		};

		

		/* Settings (continue or start new game) */
		if (this.state.page === 'settings') {
			return (
				<Settings
					isAdmin={this.state.isAdmin}
					languageId={this.props.languageId}
					email={this.props.email}
					deviceInfo={this.props.deviceInfo}
					gameData={this.state.game}
					goToPage={this.goToPage}
					setLanguageId={this.props.setLanguageId}
					handleLogout={this.props.handleLogout}
				/>
			);
		}

		if (this.state.page === 'stats') {
			return (
				<Stats 
					languageId={this.props.languageId}	
					deviceInfo={this.props.deviceInfo}			
					goToPage={this.goToPage}
				/>
			);
		}

		if (this.state.page === 'create-game') {
			return (
				<CreateGame 
					languageId={this.props.languageId}
					deviceInfo={this.props.deviceInfo}
					gameData={this.state.game}
					goToPage={this.goToPage} 
					handleLogout={this.props.handleLogout} 
					createNewGame={this.createNewGame}
				/>
			);
		}

		/* Game info */
		if (this.state.page === 'game-intro' && this.state.game) {
			return (
				<>
					<GameIntro 
						languageId={this.props.languageId} 
						deviceInfo={this.props.deviceInfo} 
						gameData={this.state.game}
						goToPage={this.goToPage} 
					/>
					<Button 
						text={getText('general_logout', this.props.languageId)}
						classes={['orange', 'logout', 'facilitator', 'positioned']}
						onClick={() => {this.props.handleLogout();}}
					/>
				</>
			);
		}

		/* Game quote */
		if (this.state.page === 'game-groups' && this.state.game) {
			return (
				<>
					<GameGroups 
						languageId={this.props.languageId}
						deviceInfo={this.props.deviceInfo} 
						gameData={this.state.game} 
						goToPage={this.goToPage}
					/>
					<Button 
						text={getText('general_logout', this.props.languageId)}
						classes={['orange', 'logout', 'facilitator', 'positioned']}
						onClick={() => {this.props.handleLogout();}}
					/>
				</>
			);
		}

		/* Game status */
		if (this.state.page === 'game-status' && this.state.game) {
			return (
				<>
					<GameStatus 
						languageId={this.props.languageId}
						deviceInfo={this.props.deviceInfo}
						gameData={this.state.game}
						groupsData={this.state.groups}
						goToPage={this.goToPage}
					/>
					<Button 
						text={getText('general_logout', this.props.languageId)}
						classes={['orange', 'logout', 'facilitator', 'positioned']}
						onClick={() => {this.props.handleLogout();}}
					/>
				</>
			);
		}

		/* Game results intro */
		if (this.state.page === 'game-results-intro' && this.state.game) {
			return (
				<>
					<GameResultsIntro
						languageId={this.props.languageId}
						deviceInfo={this.props.deviceInfo}
						goToPage={this.goToPage}
					/>
					<Button 
						text={getText('general_logout', this.props.languageId)}
						classes={['orange', 'logout', 'facilitator', 'positioned']}
						onClick={() => {this.props.handleLogout();}}
					/>
				</>
			);
		}
		/* Game results */
		if (this.state.page === 'game-results' && this.state.game) {
			return (
				<>
					<GameResults 
						languageId={this.props.languageId}
						deviceInfo={this.props.deviceInfo}
						gameData={this.state.game}
						groupsData={this.state.groups}
						goToPage={this.goToPage}
					/>
					<Button 
						text={getText('general_logout', this.props.languageId)}
						classes={['orange', 'logout', 'facilitator', 'positioned']}
						onClick={() => {this.props.handleLogout();}}
					/>
				</>
			);
		}
		
		/* Game results outro */
		if (this.state.page === 'game-results-outro' && this.state.game) {
			return (
				<>
					<GameResultsOutro
						languageId={this.props.languageId}
						deviceInfo={this.props.deviceInfo}
						gameData={this.state.game}
						groupsData={this.state.groups}
						goToPage={this.goToPage}
					/>
					<Button 
						text={getText('general_logout', this.props.languageId)}
						classes={['orange', 'logout', 'facilitator', 'positioned']}
						onClick={() => {this.props.handleLogout();}}
					/>
				</>
			);
		}

		/* Game experiment presentation */
		if (this.state.page === 'game-experiment' && this.state.game) {
			return (
				<>
					<GameExperiment
						languageId={this.props.languageId}
						deviceInfo={this.props.deviceInfo}
						gameData={this.state.game}
						goToPage={this.goToPage}
					/>
					<Button 
						text={getText('general_logout', this.props.languageId)}
						classes={['orange', 'logout', 'facilitator', 'positioned']}
						onClick={() => {this.props.handleLogout();}}
					/>
				</>
			);
		}

		/* Game experiment presentation */
		if (this.state.page === 'game-experiment-presentation' && this.state.game) {
			return (
				<>
					<GameExperimentPresentation
						languageId={this.props.languageId}
						deviceInfo={this.props.deviceInfo}
						gameData={this.state.game}
						goToPage={this.goToPage}
					/>
					<Button 
						text={getText('general_logout', this.props.languageId)}
						classes={['orange', 'logout', 'facilitator', 'positioned']}
						onClick={() => {this.props.handleLogout();}}
					/>
				</>
			);
		}

		/* Game end */
		if (this.state.page === 'game-end' && this.state.game) {
			return (
				<>
					<GameEnd 
						languageId={this.props.languageId}
						deviceInfo={this.props.deviceInfo}
						goToPage={this.goToPage}
					/>
					<Button 
						text={getText('general_logout', this.props.languageId)}
						classes={['orange', 'logout', 'facilitator', 'positioned']}
						onClick={() => {this.props.handleLogout();}}
					/>
				</>
			);
		}

		/* No match, default page is loading */
		return (
			<Loading 
				role="facilitator"
				languageId={this.props.languageId}
				handleLogout={this.props.handleLogout} 
			/>
		);
	};
}

FacilitatorController.propTypes = {
	languageId: PropTypes.string.isRequired,
	email: PropTypes.string.isRequired,
	userId: PropTypes.string.isRequired,
	deviceInfo: PropTypes.object.isRequired,
	setLanguageId: PropTypes.func.isRequired,
	handleLogout: PropTypes.func.isRequired
};

export default FacilitatorController;
